/**
 * PlanCalendar — vista calendario tipo Teams del plan. Mapea la rutina semanal
 * a fechas reales desde la fecha de inicio (ver lib/planCalendar). Se reutiliza
 * en el editor (preview del coach) y en el link público (el cliente ve qué le
 * toca hoy y cómo cae el plan en el mes).
 */
import { useMemo } from "react";
import type { RoutineWeek } from "../../types/plan";
import {
  buildCalendarMonths,
  formatPlanDate,
  parsePlanDate,
  planEndDate,
  routineForDate,
  WEEKDAY_HEADERS,
} from "../../lib/planCalendar";

interface PlanCalendarProps {
  startDate: string;
  endDate?: string;
  weeks: RoutineWeek[];
}

export function PlanCalendar({ startDate, endDate, weeks }: PlanCalendarProps) {
  const start = useMemo(() => parsePlanDate(startDate), [startDate]);
  // Fin del plan: prioriza la fecha que puso el coach; si no hay, se deriva de
  // las semanas de rutina (legacy). Así el calendario muestra la fecha real.
  const end = useMemo(
    () => parsePlanDate(endDate) || planEndDate(start, weeks.length),
    [endDate, start, weeks.length],
  );
  const months = useMemo(() => buildCalendarMonths(start, weeks, new Date(), end), [start, weeks, end]);
  const todayInfo = useMemo(() => routineForDate(new Date(), start, weeks, end), [start, weeks, end]);

  if (!start) {
    return (
      <div className="al-plancal-empty">
        Definí la <b>fecha de inicio</b> del plan (sección Cliente) para ver el calendario.
      </div>
    );
  }
  if (weeks.length === 0) {
    return (
      <div className="al-plancal-empty">
        Agregá días de rutina para ver el calendario.
      </div>
    );
  }

  const todayLabel = (() => {
    if (!todayInfo.inPlan) {
      const now = new Date();
      return now < start ? "El plan todavía no empezó" : "El plan ya terminó";
    }
    if (todayInfo.isRest) return "Hoy: Descanso 😌";
    return `Hoy: ${todayInfo.day?.focus || "Entrenamiento"}`;
  })();

  return (
    <div className="al-plancal">
      <div className="al-plancal-head">
        <div className="al-plancal-range">
          <span className="tiny muted">Plan</span>
          <b>{formatPlanDate(start)} → {formatPlanDate(end)}</b>
          <span className="tiny muted">· {weeks.length} {weeks.length === 1 ? "semana" : "semanas"}</span>
        </div>
        <div className={"al-plancal-today" + (todayInfo.inPlan && !todayInfo.isRest ? " is-train" : "")}>
          {todayLabel}
        </div>
      </div>

      {months.map((m) => (
        <div key={`${m.year}-${m.month}`} className="al-plancal-month">
          <div className="al-plancal-month-label">{m.label}</div>
          <div className="al-plancal-grid">
            {WEEKDAY_HEADERS.map((h) => (
              <div key={h} className="al-plancal-dow">{h}</div>
            ))}
            {m.cells.map((c, i) => {
              if (!c.date) return <div key={i} className="al-plancal-cell is-empty" />;
              const cls = [
                "al-plancal-cell",
                c.inPlan ? "is-in" : "is-out",
                c.isToday ? "is-today" : "",
                c.inPlan && !c.isRest ? "is-train" : "",
                c.inPlan && c.isRest ? "is-rest" : "",
              ].join(" ");
              return (
                <div key={i} className={cls} title={c.focus || (c.isRest ? "Descanso" : "")}>
                  <span className="al-plancal-num">{c.date.getDate()}</span>
                  {c.inPlan && (
                    <span className="al-plancal-focus">
                      {c.isRest ? "Descanso" : (c.focus || "Entreno")}
                    </span>
                  )}
                </div>
              );
            })}
          </div>
        </div>
      ))}

      <div className="al-plancal-legend tiny muted">
        <span><i className="al-plancal-dot is-train" /> Entrenamiento</span>
        <span><i className="al-plancal-dot is-rest" /> Descanso</span>
        <span><i className="al-plancal-dot is-today" /> Hoy</span>
      </div>
    </div>
  );
}
